import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { USER_TIERS } from '../../../constants/tiers';
import preferences from '../../../data/preferences';
import { getElementDescription } from '../../../data/elementDescriptions';
import CosmicIcon from '../../common/CosmicIcon';
import './CosmicConstellationTab.css';

/**
 * Get the proper descriptor for an element ID from preferences data
 * This returns clean names like "Analyst" instead of "Analysis & Pattern Recognition"
 */
const getElementDescriptor = (elementId) => {
  if (!elementId) return 'Unknown';
  
  // Search through all domain preferences
  const allPrefs = preferences.getAllPreferences ? preferences.getAllPreferences() : [];
  const found = allPrefs.find(p => p.id === elementId);
  
  if (found?.descriptor) return found.descriptor;
  
  // Fallback: search manually through domains
  for (const domainId of ['insight', 'vision', 'connection', 'impact', 'structure', 'crossDomain']) {
    const domainPrefs = preferences[domainId];
    if (Array.isArray(domainPrefs)) {
      const element = domainPrefs.find(p => p.id === elementId);
      if (element?.descriptor) return element.descriptor;
    }
  }
  
  // Ultimate fallback: capitalize the elementId
  return elementId.replace(/([A-Z])/g, ' $1').trim();
};

/**
 * Cosmic Constellation Tab - Interactive archetype display with domain-colored stars
 * Features the same beautiful patterns from the homepage, but personalized with user's scores
 */
const CosmicConstellationTab = ({ results, tier, domainColors, hideStarGrid = false }) => {
  const [selectedStar, setSelectedStar] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('forming');
  const [hoveredStar, setHoveredStar] = useState(null);
  const [pulsingDomain, setPulsingDomain] = useState(null); // For domain pulse effect
  
  // Get top 2 domain IDs for Free tier restrictions
  const topTwoDomainIds = useMemo(() => {
    if (!results?.domains) return [];
    return Object.entries(results.domains)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([domainId]) => domainId);
  }, [results?.domains]);
  
  // Get the user's archetype based on top 2 domains
  const userArchetype = useMemo(() => {
    if (!results?.domains) return archetypePatterns[0];
    
    const topDomains = Object.entries(results.domains)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 2)
      .map(([id]) => id);
    
    // Find matching archetype
    const matchedArchetype = archetypePatterns.find(arch => 
      arch.domains.includes(topDomains[0]) && arch.domains.includes(topDomains[1])
    );
    
    return matchedArchetype || archetypePatterns[0];
  }, [results?.domains]);
  
  // Pre-compute unlocked element IDs using score-based logic
  const unlockedElementIds = useMemo(() => {
    if (tier === USER_TIERS.PREMIUM) return null; // Premium sees all
    if (!results?.topPreferences) return new Set();
    
    const allElements = [...results.topPreferences]
      .map(el => ({
        ...el,
        normalizedScore: el.strengthPercentile || (el.score > 1 ? el.score : el.score * 100)
      }))
      .sort((a, b) => b.normalizedScore - a.normalizedScore);
    
    if (tier === USER_TIERS.ENHANCED) {
      // Enhanced: Top 8 scoring elements across ALL domains (full constellation)
      return new Set(allElements.slice(0, 8).map(el => el.id || el.elementId));
    }
    
    // Free tier: Top 3 scoring elements within top 2 domains only
    const elementsInTop2 = allElements.filter(el => topTwoDomainIds.includes(el.domainId));
    return new Set(elementsInTop2.slice(0, 3).map(el => el.id || el.elementId));
  }, [tier, results?.topPreferences, topTwoDomainIds]);
  
  // Check if a star's score is unlocked
  const isStarUnlocked = (star) => {
    if (tier === USER_TIERS.PREMIUM) return true;
    const starId = star.id || star.elementId;
    return unlockedElementIds?.has(starId) || false;
  };
  
  // Get user's top elements mapped to star positions
  const userStars = useMemo(() => {
    if (!userArchetype?.starPositions) return [];

    // Premium constellations can have shapes with more than 8 stars. topPreferences
    // is typically capped at the top 8, which would leave the extra positions empty
    // for Premium takers. When the full 45-element set is available (Premium), source
    // the stars from allElements so every position in the shape gets lit.
    const sourcePool = (tier === USER_TIERS.PREMIUM && Array.isArray(results?.allElements) && results.allElements.length)
      ? results.allElements
      : results?.topPreferences;
    if (!sourcePool || !sourcePool.length) return [];

    const allElements = [...sourcePool].sort((a, b) => {
      // Use strengthPercentile if available, otherwise convert score
      const scoreA = a.strengthPercentile || (a.score > 1 ? a.score : a.score * 100);
      const scoreB = b.strengthPercentile || (b.score > 1 ? b.score : b.score * 100);
      return scoreB - scoreA;
    });
    const starCount = userArchetype.starPositions.length;
    
    // Take top elements to match number of stars in pattern
    return allElements.slice(0, starCount).map((element, index) => {
      // Normalize score to percentage (0-100)
      const normalizedScore = element.strengthPercentile || 
        (element.score > 1 ? element.score : element.score * 100);
      
      // Get position with fallback
      const starPosition = userArchetype.starPositions[index] || { x: 50, y: 50, primary: false };
      
      // Get the proper descriptor from preferences data (clean names without ampersands)
      const elementId = element.id || element.elementId;
      const personDescriptor = getElementDescriptor(elementId);
      
      return {
        ...element,
        domainId: element.domainId || element.domain,
        personDescriptor, // Clean name like "Analyst", "Strategist", etc.
        score: normalizedScore,
        position: {
          x: starPosition.x,
          y: starPosition.y
        },
        isPrimary: starPosition.primary || false,
        globalRank: index + 1
      };
    });
  }, [results?.topPreferences, results?.allElements, tier, userArchetype]);
  
  // Animation sequence
  useEffect(() => {
    const timer1 = setTimeout(() => setAnimationPhase('stars'), 300);
    const timer2 = setTimeout(() => setAnimationPhase('connections'), 800);
    const timer3 = setTimeout(() => setAnimationPhase('labels'), 1500);
    const timer4 = setTimeout(() => setAnimationPhase('complete'), 2200);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);
  
  const handleStarClick = (star) => {
    setSelectedStar(star);
    // Trigger domain pulse effect - same-domain stars glow together
    if (star.domainId) {
      setPulsingDomain(star.domainId);
      // Clear the pulse after animation completes
      setTimeout(() => setPulsingDomain(null), 1500);
    }
  };
  
  const handleStarHover = (star) => {
    setHoveredStar(star);
  };
  
  // Count elements in each domain for synergy message
  const domainElementCounts = useMemo(() => {
    if (!results?.topPreferences) return {};
    return results.topPreferences.reduce((acc, el) => {
      acc[el.domainId] = (acc[el.domainId] || 0) + 1;
      return acc;
    }, {});
  }, [results?.topPreferences]);
  
  // Get synergy message for a domain
  const getDomainSynergyMessage = (domainId) => {
    const count = domainElementCounts[domainId] || 0;
    const domainName = domainId?.charAt(0).toUpperCase() + domainId?.slice(1);
    if (count >= 3) {
      return `You have ${count} elements in ${domainName} — this is a strong theme in your constellation.`;
    } else if (count === 2) {
      return `${count} elements share the ${domainName} domain, forming a connected pair.`;
    }
    return `This element is part of your ${domainName} domain.`;
  };

  // Element detail modal. Rendered inline in the normal view, and *inside* the
  // fullscreen portal when fullscreen so it reliably stacks above the
  // constellation (a separate body portal could sit behind the fullscreen layer).
  const renderStarModal = () => (
    <div className="cosmic-modal-overlay" onClick={() => setSelectedStar(null)}>
      <div className={`cosmic-element-modal ${selectedStar.isScoreLocked ? 'cosmic-element-modal--locked' : ''}`} onClick={e => e.stopPropagation()}>
        <button className="cosmic-modal-close" onClick={() => setSelectedStar(null)}>✕</button>

        {/* Star Visual - unlocked shows rank, locked shows lock icon */}
        <div className="cosmic-modal-star-visual">
          {selectedStar.isScoreLocked ? (
            <div className="cosmic-modal-star-orb cosmic-modal-star-orb--locked">
              <span className="cosmic-modal-lock-icon"><CosmicIcon name="ui_locked" size={28} decorative /></span>
            </div>
          ) : (
            <>
              <div
                className="cosmic-modal-star-orb"
                style={{ '--star-color': domainColors[selectedStar.domainId] }}
              >
                <span className="cosmic-modal-score">
                  {`#${selectedStar.globalRank}`}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Element name - only visible if unlocked */}
        <h3 className="cosmic-modal-title">
          {selectedStar.isScoreLocked ? 'Locked Element' : (selectedStar.personDescriptor || selectedStar.name)}
        </h3>

        {/* Domain badge - only show for unlocked */}
        {!selectedStar.isScoreLocked && (
          <div
            className="cosmic-modal-domain-badge"
            style={{ '--domain-color': domainColors[selectedStar.domainId] }}
          >
            <CosmicIcon name={`domain_${selectedStar.domainId}`} size={16} mono decorative /> {selectedStar.domainId?.charAt(0).toUpperCase() + selectedStar.domainId?.slice(1)}
          </div>
        )}

        {/* Description - only visible if unlocked */}
        {!selectedStar.isScoreLocked ? (
          <>
            <p className="cosmic-modal-description">
              {selectedStar.fullDescription || selectedStar.description || getElementDescription(selectedStar.id || selectedStar.elementId, selectedStar.domainId) ||
               `This element represents your motivation for ${selectedStar.personDescriptor || selectedStar.name}. It's one of your key drivers in your career constellation.`}
            </p>

            {/* Synergy message - shows how elements connect */}
            <div className="cosmic-modal-synergy" style={{ '--domain-color': domainColors[selectedStar.domainId] }}>
              <span className="cosmic-modal-synergy-icon"><CosmicIcon name="brand_spark" size={16} decorative /></span>
              <span className="cosmic-modal-synergy-text">{getDomainSynergyMessage(selectedStar.domainId)}</span>
            </div>

            {/* Free tier: show rank + conversion message; Enhanced/Premium: show strength bar */}
            {tier === USER_TIERS.FREE ? (
              <div className="cosmic-modal-strength">
                <div className="cosmic-modal-strength-label">Your #{selectedStar.globalRank} Strength</div>
                <p style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '13px',
                  margin: '8px 0 0',
                  lineHeight: '1.4',
                  textAlign: 'center'
                }}>
                  Your top elements are closely ranked — Enhanced measurement reveals precise percentages and true ordering.
                </p>
              </div>
            ) : (
              <div className="cosmic-modal-strength">
                <div className="cosmic-modal-strength-label">Strength Score</div>
                <div className="cosmic-modal-strength-bar">
                  <div
                    className="cosmic-modal-strength-fill"
                    style={{
                      width: `${selectedStar.score}%`,
                      background: `linear-gradient(90deg, ${domainColors[selectedStar.domainId]}, color-mix(in srgb, ${domainColors[selectedStar.domainId]} 70%, white))`
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="cosmic-modal-locked-section">
            <div className="cosmic-modal-locked-message">
              Your score for this element is locked
            </div>
            <button className="cosmic-cta cosmic-cta--sm">
              <CosmicIcon name="ui_bolt" size={16} decorative /> Upgrade to {tier === USER_TIERS.FREE ? 'Enhanced' : 'Premium'} to Unlock
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="cosmic-constellation-tab">
      
      {/* Interactive Archetype Constellation Card */}
      <div className="cosmic-card cosmic-card--elevated cosmic-archetype-card">
        <div className="cosmic-card__header">
          <div className="cosmic-archetype-title-row">
            <h2 className="cosmic-archetype-name"><CosmicIcon name={`archetype_${userArchetype.id}`} size={30} className="cosmic-archetype-emoji" decorative /> The {userArchetype.name}</h2>
            <div className="cosmic-archetype-domains">
              {userArchetype.domains.map(domain => (
                <span 
                  key={domain}
                  className="cosmic-domain-pill"
                  style={{ '--domain-color': domainColors[domain] }}
                >
                  <CosmicIcon name={`domain_${domain}`} size={16} mono style={{ color: '#fff' }} decorative /> {domain.charAt(0).toUpperCase() + domain.slice(1)}
                </span>
              ))}
            </div>
          </div>
          <p className="cosmic-archetype-description">{userArchetype.description}</p>
        </div>
        
        <div className="cosmic-card__body">
          <div className="cosmic-archetype-viewer">
            
            {/* The Main Constellation SVG */}
            <svg 
              viewBox="0 0 100 100" 
              className={`cosmic-archetype-svg phase-${animationPhase}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {/* Glow filters for each domain */}
                {Object.entries(domainColors).map(([domain, color]) => (
                  <filter key={domain} id={`archetype-glow-${domain}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.5" result="blur"/>
                    <feFlood floodColor={color} floodOpacity="0.6"/>
                    <feComposite in2="blur" operator="in"/>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                ))}
                
                {/* Star gradient */}
                <radialGradient id="star-inner-glow" cx="30%" cy="30%">
                  <stop offset="0%" stopColor="white" stopOpacity="0.9"/>
                  <stop offset="100%" stopColor="white" stopOpacity="0"/>
                </radialGradient>
              </defs>
              
              {/* Background starfield */}
              <g className="cosmic-starfield">
                {[...Array(20)].map((_, i) => (
                  <circle
                    key={`bg-star-${i}`}
                    cx={10 + (i * 4.5) % 80}
                    cy={15 + (i * 7.3) % 70}
                    r={0.3 + (i % 3) * 0.2}
                    fill="rgba(255,255,255,0.3)"
                    className="cosmic-bg-star"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </g>
              
              {/* Connection Lines */}
              <g className="cosmic-archetype-connections">
                {userArchetype.connections.map((conn, index) => {
                  const fromStar = userStars[conn.from];
                  const toStar = userStars[conn.to];
                  if (!fromStar?.position || !toStar?.position) return null;
                  
                  const fromX = fromStar.position.x ?? 50;
                  const fromY = fromStar.position.y ?? 50;
                  const toX = toStar.position.x ?? 50;
                  const toY = toStar.position.y ?? 50;
                  
                  return (
                    <line
                      key={`conn-${index}`}
                      x1={fromX}
                      y1={fromY}
                      x2={toX}
                      y2={toY}
                      stroke={`url(#gradient-${index})`}
                      strokeWidth={conn.primary ? 1.5 : 0.8}
                      className={`cosmic-archetype-line ${conn.primary ? 'primary' : ''}`}
                      style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                    />
                  );
                })}
                
                {/* Connection gradients */}
                {userArchetype.connections.map((conn, index) => {
                  const fromStar = userStars[conn.from];
                  const toStar = userStars[conn.to];
                  if (!fromStar?.position || !toStar?.position) return null;
                  
                  const fromX = fromStar.position.x ?? 50;
                  const fromY = fromStar.position.y ?? 50;
                  const toX = toStar.position.x ?? 50;
                  const toY = toStar.position.y ?? 50;
                  
                  return (
                    <defs key={`gradient-def-${index}`}>
                      <linearGradient 
                        id={`gradient-${index}`}
                        x1={fromX} y1={fromY}
                        x2={toX} y2={toY}
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop offset="0%" stopColor={domainColors[fromStar.domainId] || '#FFD700'} stopOpacity="0.7"/>
                        <stop offset="100%" stopColor={domainColors[toStar.domainId] || '#FFD700'} stopOpacity="0.7"/>
                      </linearGradient>
                    </defs>
                  );
                })}
              </g>
              
              {/* Stars (Elements) - Size based purely on score */}
              <g className="cosmic-archetype-stars">
                {userStars.map((star, index) => {
                  const unlocked = isStarUnlocked(star);
                  // Use domain color for unlocked, gray for locked
                  const starColor = unlocked ? (domainColors[star.domainId] || '#FFD700') : '#4a4a4a';
                  
                  // Star size: smaller overall (1.2-2.5 range)
                  // Locked stars are nearly the same size (1.2 vs 1.2-2.5)
                  const minSize = 1.2;
                  const maxSize = 2.5;
                  const starSize = unlocked 
                    ? minSize + ((star.score / 100) * (maxSize - minSize))
                    : minSize + 0.3; // Locked stars slightly smaller but visible
                  
                  const isHovered = hoveredStar?.id === star.id;
                  const isSelected = selectedStar?.id === star.id;
                  const isPulsing = pulsingDomain === star.domainId && unlocked;
                  
                  // Ensure position exists with fallback
                  const posX = star.position?.x ?? 50;
                  const posY = star.position?.y ?? 50;
                  
                  return (
                    <g 
                      key={star.id || `star-${index}`}
                      className={`cosmic-archetype-star ${isHovered ? 'hovered' : ''} ${isSelected ? 'selected' : ''} ${!unlocked ? 'locked' : ''} ${isPulsing ? 'pulsing' : ''}`}
                      transform={`translate(${posX}, ${posY})`}
                      onClick={() => handleStarClick({ ...star, isScoreLocked: !unlocked })}
                      onMouseEnter={() => handleStarHover(star)}
                      onMouseLeave={() => setHoveredStar(null)}
                      style={{ cursor: 'pointer', opacity: unlocked ? 1 : 0.5 }}
                    >
                      {/* Soft outer glow - subtle, based on score (only for unlocked) */}
                      {unlocked && (
                        <circle
                          r={starSize * 2}
                          fill={starColor}
                          opacity={0.1 + (star.score / 100) * 0.1}
                          className="cosmic-star-outer-glow"
                        />
                      )}
                      
                      {/* Main star body */}
                      <circle
                        r={starSize}
                        fill={starColor}
                        filter={unlocked ? `url(#archetype-glow-${star.domainId})` : undefined}
                        className="cosmic-star-body"
                      />
                      
                      {/* Inner highlight for depth (only for unlocked) */}
                      {unlocked && (
                        <circle
                          r={starSize * 0.35}
                          fill="white"
                          opacity="0.6"
                          cx={-starSize * 0.15}
                          cy={-starSize * 0.15}
                          className="cosmic-star-highlight"
                        />
                      )}
                      
                      {/* Touch target (invisible) */}
                      <circle
                        r={Math.max(starSize + 4, 7)}
                        fill="transparent"
                        className="cosmic-touch-target"
                      />
                    </g>
                  );
                })}
              </g>
              
              {/* Hover Label - shows "Locked" for locked elements */}
              {hoveredStar && (() => {
                const isHoveredStarUnlocked = isStarUnlocked(hoveredStar);
                return (
                  <g 
                    className="cosmic-hover-label"
                    transform={`translate(${hoveredStar.position.x}, ${hoveredStar.position.y - 10})`}
                  >
                    <rect
                      x="-20"
                      y="-7"
                      width="40"
                      height="14"
                      rx="3"
                      fill="rgba(0,0,0,0.8)"
                      stroke={isHoveredStarUnlocked ? domainColors[hoveredStar.domainId] : '#666'}
                      strokeWidth="0.5"
                    />
                    <text
                      y="1"
                      fontSize="3"
                      fill="white"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {isHoveredStarUnlocked ? (hoveredStar.personDescriptor || hoveredStar.name) : 'Locked'}
                    </text>
                    <text
                      y="5"
                      fontSize="2.5"
                      fill={isHoveredStarUnlocked ? domainColors[hoveredStar.domainId] : '#888'}
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {isHoveredStarUnlocked 
                        ? `#${hoveredStar.globalRank} Strength`
                        : 'Upgrade to see'}
                    </text>
                  </g>
                );
              })()}
              
            </svg>
            
            {/* Explore Button - Fullscreen style with cyan edges */}
            <button 
              className="cosmic-explore-btn"
              onClick={() => setIsFullscreen(true)}
            >
              <span className="cosmic-explore-icon">⤢</span>
              <span className="cosmic-explore-text">Explore Constellation</span>
              <span className="cosmic-explore-icon">⤢</span>
            </button>
            
          </div>
        </div>
      </div>
      
      {/* Fullscreen Portal - Renders outside DOM hierarchy */}
      {isFullscreen && createPortal(
        <div className="cosmic-fullscreen-portal">
          <button 
            className="cosmic-exit-fullscreen"
            onClick={() => setIsFullscreen(false)}
          >
            ✕ Exit
          </button>
          
          <div className="cosmic-fullscreen-content">
            <h2 className="cosmic-fullscreen-title">
              <CosmicIcon name={`archetype_${userArchetype.id}`} size={72} decorative /> {userArchetype.name}
            </h2>
            
            <svg 
              viewBox="0 0 100 100" 
              className="cosmic-fullscreen-svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {Object.entries(domainColors).map(([domain, color]) => (
                  <filter key={domain} id={`fullscreen-glow-${domain}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.5" result="blur"/>
                    <feFlood floodColor={color} floodOpacity="0.6"/>
                    <feComposite in2="blur" operator="in"/>
                    <feMerge>
                      <feMergeNode/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                ))}
              </defs>
              
              {/* Background starfield */}
              <g className="cosmic-starfield">
                {[...Array(30)].map((_, i) => (
                  <circle
                    key={`fs-bg-star-${i}`}
                    cx={5 + (i * 3.2) % 90}
                    cy={5 + (i * 5.7) % 90}
                    r={0.2 + (i % 4) * 0.15}
                    fill="rgba(255,255,255,0.4)"
                    className="cosmic-bg-star"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </g>
              
              {/* Connection Lines */}
              <g className="cosmic-archetype-connections">
                {userArchetype.connections.map((conn, index) => {
                  const fromStar = userStars[conn.from];
                  const toStar = userStars[conn.to];
                  if (!fromStar?.position || !toStar?.position) return null;
                  
                  return (
                    <line
                      key={`fs-conn-${index}`}
                      x1={fromStar.position.x ?? 50}
                      y1={fromStar.position.y ?? 50}
                      x2={toStar.position.x ?? 50}
                      y2={toStar.position.y ?? 50}
                      stroke={domainColors[fromStar.domainId] || '#FFD700'}
                      strokeWidth={conn.primary ? 1.2 : 0.6}
                      strokeOpacity={0.6}
                      className={`cosmic-archetype-line ${conn.primary ? 'primary' : ''}`}
                    />
                  );
                })}
              </g>
              
              {/* Stars */}
              <g className="cosmic-archetype-stars">
                {userStars.map((star, index) => {
                  const unlocked = isStarUnlocked(star);
                  const starColor = unlocked ? (domainColors[star.domainId] || '#FFD700') : '#4a4a4a';
                  const minSize = 1.5;
                  const maxSize = 3.5;
                  const starSize = unlocked 
                    ? minSize + ((star.score / 100) * (maxSize - minSize))
                    : minSize + 0.3;
                  const posX = star.position?.x ?? 50;
                  const posY = star.position?.y ?? 50;
                  const isHoveredFs = hoveredStar?.id === star.id;
                  const isPulsingFs = pulsingDomain === star.domainId && unlocked;
                  
                  return (
                    <g 
                      key={star.id || `fs-star-${index}`}
                      transform={`translate(${posX}, ${posY})`}
                      onClick={() => handleStarClick({ ...star, isScoreLocked: !unlocked })}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(null)}
                      style={{ cursor: 'pointer' }}
                      className={`cosmic-archetype-star ${!unlocked ? 'locked' : ''} ${isHoveredFs ? 'hovered' : ''} ${isPulsingFs ? 'pulsing' : ''}`}
                    >
                      <circle
                        r={starSize * 1.8}
                        fill={starColor}
                        opacity={isPulsingFs ? 0.4 : 0.15}
                        className="cosmic-star-outer-glow"
                      />
                      <circle
                        r={starSize}
                        fill={starColor}
                        className="cosmic-star-body"
                        filter={unlocked ? `url(#fullscreen-glow-${star.domainId})` : undefined}
                      />
                      <circle
                        r={starSize * 0.4}
                        fill="white"
                        opacity={unlocked ? 0.7 : 0.3}
                        className="cosmic-star-highlight"
                      />
                      {/* Invisible enlarged hit area — makes the small fullscreen stars reliably clickable */}
                      <circle
                        r={Math.max(starSize + 4, 7)}
                        fill="transparent"
                        className="cosmic-touch-target"
                      />
                    </g>
                  );
                })}
              </g>
              
              {/* Hover Label for Fullscreen Stars */}
              {hoveredStar && (() => {
                const posX = hoveredStar.position?.x ?? 50;
                const posY = hoveredStar.position?.y ?? 50;
                const unlocked = isStarUnlocked(hoveredStar);
                const labelText = unlocked 
                  ? (hoveredStar.personDescriptor || hoveredStar.name || 'Element')
                  : 'Locked';
                
                return (
                  <g 
                    transform={`translate(${posX}, ${posY - 6})`}
                    style={{ pointerEvents: 'none' }}
                  >
                    <rect
                      x={-20}
                      y={-4}
                      width={40}
                      height={7}
                      rx={1.5}
                      fill="rgba(0,0,0,0.75)"
                      stroke={unlocked ? domainColors[hoveredStar.domainId] : 'rgba(255,255,255,0.3)'}
                      strokeWidth={0.3}
                    />
                    <text
                      x={0}
                      y={1.5}
                      textAnchor="middle"
                      fill="white"
                      fontSize="2.5"
                      fontWeight="600"
                    >
                      {labelText.length > 12 ? labelText.slice(0, 12) + '...' : labelText}
                    </text>
                  </g>
                );
              })()}
            </svg>
            
            <div className="cosmic-fullscreen-legend">
              {Object.entries(domainColors).slice(0, 5).map(([domain, color]) => (
                <span key={domain} className="cosmic-fullscreen-legend-item" style={{ '--domain-color': color }}>
                  <CosmicIcon name={`domain_${domain}`} size={16} decorative /> {domain.charAt(0).toUpperCase() + domain.slice(1)}
                </span>
              ))}
            </div>
          </div>

          {/* Element modal lives inside the fullscreen portal so a star tap opens
              it above the constellation (and persists) in this view too. */}
          {selectedStar && renderStarModal()}
        </div>,
        document.body
      )}
      
      {/* Star Grid - Can be hidden when rendered separately */}
      {!hideStarGrid && (
        <div className="cosmic-card cosmic-card--professional cosmic-star-grid-card">
          <div className="cosmic-card__header">
            <h3 className="cosmic-card__title"><CosmicIcon name="brand_spark" size={20} decorative /> Your Constellation Stars</h3>
            <p className="cosmic-card__subtitle">
              Each star represents one of your motivated elements. Tap to learn more.
            </p>
          </div>
          <div className="cosmic-card__body">
            <div className="cosmic-star-grid">
              {userStars.slice(0, tier === USER_TIERS.PREMIUM ? 8 : tier === USER_TIERS.ENHANCED ? 8 : 3).map((star, index) => (
                <div 
                  key={star.id}
                  className={`cosmic-star-grid-item ${selectedStar?.id === star.id ? 'selected' : ''} ${star.globalRank <= 5 ? 'top-five' : ''}`}
                  onClick={() => handleStarClick(star)}
                  style={{ '--domain-color': domainColors[star.domainId] }}
                >
                  <div className="cosmic-star-grid-orb">
                    <div 
                      className="cosmic-star-grid-glow"
                      style={{ 
                        background: domainColors[star.domainId],
                        transform: `scale(${0.8 + (star.score / 100) * 0.4})`
                      }}
                    />
                    <span className="cosmic-star-grid-score">
                      {`#${star.globalRank}`}
                    </span>
                  </div>
                  <div className="cosmic-star-grid-name">{star.personDescriptor || star.name}</div>
                  <div 
                    className="cosmic-star-grid-domain"
                    style={{ color: domainColors[star.domainId] }}
                  >
                    <CosmicIcon name={`domain_${star.domainId}`} size={14} decorative /> {star.domainId}
                  </div>
                </div>
              ))}
            </div>
            
            {tier === USER_TIERS.FREE && (
              <div className="cosmic-legend-upgrade">
                <p><CosmicIcon name="ui_locked" size={14} decorative /> Upgrade to see more of your constellation stars</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Element Detail Modal - inline in the normal view. In fullscreen it's
          rendered inside the fullscreen portal (above) so it stacks correctly. */}
      {selectedStar && !isFullscreen && renderStarModal()}
      
    </div>
  );
};

// Archetype patterns with star positions and connections
// Based on the homepage constellation patterns
const archetypePatterns = [
  {
    id: 'luminary',
    name: 'Luminary',
    domains: ['insight', 'vision'],
    description: `Luminaries have minds that grasp how things work and can easily envision what those things could become. While everyone else is still hunting for coffee and remembering what day it is, you are able to move naturally between deep analysis and bold possibilities. In addition to diagnosing what's happening now, you light a path toward better futures. People orient around you the way travelers once steered by the brightest stars. The clarity you shine gives others the confidence to move. Your ability to turn insight into inspiration helps everyone around you see further than they could on their own.`,
    starPositions: [
      { x: 50, y: 15, primary: true },
      { x: 35, y: 35, primary: false },
      { x: 65, y: 35, primary: false },
      { x: 25, y: 50, primary: false },
      { x: 50, y: 45, primary: true },
      { x: 75, y: 50, primary: false },
      { x: 30, y: 70, primary: false },
      { x: 50, y: 75, primary: true },
      { x: 70, y: 70, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: true },
      { from: 0, to: 4, primary: true },
      { from: 1, to: 3, primary: false },
      { from: 2, to: 5, primary: false },
      { from: 4, to: 7, primary: true },
      { from: 7, to: 6, primary: false },
      { from: 7, to: 8, primary: false }
    ]
  },
  {
    id: 'trailblazer',
    name: 'Trailblazer',
    domains: ['vision', 'impact'],
    description: `Trailblazers spot paths where everyone else sees walls, and then have the nerve to start walking while the committee is still asking whether it's allowed. You pair big vision with the drive to move, so your ideas become motion instead of fading into daydreams. You go first, take the early arrows, and leave trails wide enough for others to follow. Like the first bright stars out at dusk, you give the rest of us something to steer toward. Your "what ifs" have a way of turning into movements that other people join.`,
    starPositions: [
      { x: 15, y: 25, primary: true },
      { x: 35, y: 30, primary: false },
      { x: 55, y: 25, primary: false },
      { x: 75, y: 35, primary: false },
      { x: 85, y: 50, primary: true },
      { x: 65, y: 60, primary: false },
      { x: 45, y: 70, primary: false },
      { x: 25, y: 75, primary: false },
      { x: 50, y: 85, primary: true }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 1, to: 2, primary: true },
      { from: 2, to: 3, primary: true },
      { from: 3, to: 4, primary: true },
      { from: 4, to: 5, primary: true },
      { from: 5, to: 6, primary: true },
      { from: 6, to: 7, primary: true },
      { from: 7, to: 8, primary: true }
    ]
  },
  {
    id: 'spark',
    name: 'Spark',
    domains: ['connection', 'impact'],
    description: `Sparks are the reason flat rooms suddenly have energy, the people who walk in and somehow everyone sits up a little straighter. You blend a real gift for connection with the drive to make things happen, so you bring people together and get them moving toward something that matters. You light fuses and fan flames, turning loose groups of individuals into forces. Like a single star that makes a whole constellation suddenly legible, you connect the points until the picture means something. That warmth of yours has a way of becoming momentum the whole group can feel.`,
    starPositions: [
      { x: 50, y: 30, primary: true },
      { x: 30, y: 20, primary: false },
      { x: 70, y: 20, primary: false },
      { x: 20, y: 40, primary: false },
      { x: 80, y: 40, primary: false },
      { x: 35, y: 55, primary: true },
      { x: 65, y: 55, primary: true },
      { x: 25, y: 70, primary: false },
      { x: 50, y: 75, primary: false },
      { x: 75, y: 70, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: true },
      { from: 0, to: 3, primary: false },
      { from: 0, to: 4, primary: false },
      { from: 0, to: 5, primary: true },
      { from: 0, to: 6, primary: true },
      { from: 5, to: 7, primary: false },
      { from: 5, to: 8, primary: false },
      { from: 6, to: 8, primary: false },
      { from: 6, to: 9, primary: false }
    ]
  },
  {
    id: 'shaper',
    name: 'Shaper',
    domains: ['structure', 'vision'],
    description: `Shapers can picture a cathedral and also draw the blueprints that get it built. Most visionaries lose interest the moment the real work starts, but that is exactly where you lean in. You love the daydream and the to-do list in equal measure, turning "wouldn't it be amazing if" into a plan with dates on it. You give wild ideas the bones they need to stand up. Like the gravity that keeps a whole system of planets in orbit, you're the force that holds a bright vision in formation. The dreams other people only describe are the ones you make buildable.`,
    starPositions: [
      { x: 30, y: 20, primary: false },
      { x: 50, y: 20, primary: true },
      { x: 70, y: 20, primary: false },
      { x: 35, y: 35, primary: false },
      { x: 65, y: 35, primary: false },
      { x: 40, y: 50, primary: true },
      { x: 60, y: 50, primary: true },
      { x: 30, y: 65, primary: false },
      { x: 50, y: 70, primary: true },
      { x: 70, y: 65, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 1, to: 2, primary: true },
      { from: 0, to: 3, primary: false },
      { from: 2, to: 4, primary: false },
      { from: 3, to: 5, primary: true },
      { from: 4, to: 6, primary: true },
      { from: 5, to: 6, primary: false },
      { from: 5, to: 7, primary: false },
      { from: 5, to: 8, primary: true },
      { from: 6, to: 8, primary: true },
      { from: 6, to: 9, primary: false },
      { from: 7, to: 8, primary: false },
      { from: 8, to: 9, primary: false }
    ]
  },
  {
    id: 'navigator',
    name: 'Navigator',
    domains: ['insight', 'impact'],
    description: `Navigators are the first call when plans fall apart and the fog rolls in. You combine a clear read on what's gone wrong with the influence to move a crowd through it. You understand hard situations and carry others across them. You stay calm in the murk, find the route, and keep everyone moving while the rest of the world is ready to panic. Like the stars sailors once trusted to steer home, you're who others follow when the way is unclear. The confusion that freezes most people becomes, in your hands, a set of confident next steps.`,
    starPositions: [
      { x: 40, y: 15, primary: true },
      { x: 60, y: 15, primary: true },
      { x: 25, y: 35, primary: false },
      { x: 50, y: 45, primary: true },
      { x: 75, y: 35, primary: false },
      { x: 35, y: 65, primary: false },
      { x: 65, y: 65, primary: false },
      { x: 50, y: 80, primary: true }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: false },
      { from: 1, to: 4, primary: false },
      { from: 2, to: 3, primary: true },
      { from: 3, to: 4, primary: true },
      { from: 2, to: 5, primary: false },
      { from: 3, to: 5, primary: false },
      { from: 3, to: 6, primary: false },
      { from: 4, to: 6, primary: false },
      { from: 5, to: 7, primary: true },
      { from: 6, to: 7, primary: true }
    ]
  },
  {
    id: 'scholar',
    name: 'Scholar',
    domains: ['insight', 'structure'],
    description: `Scholars want to understand a thing all the way down, the ones still reading the footnotes after everyone else has declared the matter settled. You pair analytical horsepower with a love of order, and it shows in work that is organized, rigorous, and built to last. You uncover the patterns, test them against the evidence, and turn what you learn into knowledge others can build on. Like a star catalog that turns a scatter of lights into a map you can navigate by, you take a tangled subject and lay it out so the rest of us can find our way through. What used to feel impossibly complicated turns clear once you've made sense of it.`,
    starPositions: [
      { x: 50, y: 15, primary: true },
      { x: 35, y: 25, primary: false },
      { x: 65, y: 25, primary: false },
      { x: 20, y: 35, primary: false },
      { x: 50, y: 30, primary: true },
      { x: 80, y: 35, primary: false },
      { x: 30, y: 50, primary: false },
      { x: 50, y: 60, primary: true },
      { x: 70, y: 50, primary: false },
      { x: 50, y: 75, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: true },
      { from: 1, to: 3, primary: false },
      { from: 1, to: 4, primary: false },
      { from: 2, to: 4, primary: false },
      { from: 2, to: 5, primary: false },
      { from: 4, to: 6, primary: true },
      { from: 4, to: 7, primary: true },
      { from: 4, to: 8, primary: true },
      { from: 7, to: 9, primary: false }
    ]
  },
  {
    id: 'storykeeper',
    name: 'Storykeeper',
    domains: ['connection', 'insight'],
    description: `Storykeepers are the ones who remember why a decision was made in the first place, long after everyone else has forgotten the meeting even happened. You blend a deep read on people with sharp insight, so you catch the meaning underneath events and hold onto it for when it counts again. You tell the stories that help groups understand themselves, turning hard-won experience into wisdom the next person gets to inherit. Like an old constellation that has carried the same myths across a thousand years of nights, you keep what matters from slipping away. Years later, when a group looks back at how it became what it is, that is the story you kept alive.`,
    starPositions: [
      { x: 40, y: 20, primary: false },
      { x: 60, y: 20, primary: false },
      { x: 35, y: 35, primary: false },
      { x: 50, y: 30, primary: true },
      { x: 65, y: 35, primary: false },
      { x: 30, y: 50, primary: false },
      { x: 50, y: 45, primary: true },
      { x: 70, y: 50, primary: false },
      { x: 40, y: 65, primary: true },
      { x: 60, y: 65, primary: true }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: false },
      { from: 0, to: 3, primary: true },
      { from: 1, to: 3, primary: true },
      { from: 1, to: 4, primary: false },
      { from: 2, to: 5, primary: false },
      { from: 3, to: 6, primary: true },
      { from: 4, to: 7, primary: false },
      { from: 5, to: 8, primary: false },
      { from: 6, to: 8, primary: true },
      { from: 6, to: 9, primary: true },
      { from: 7, to: 9, primary: false }
    ]
  },
  {
    id: 'harborer',
    name: 'Harborer',
    domains: ['structure', 'connection'],
    description: `Harborers are the steady ground others stand on, the calm ports people make for when the weather turns rough. You combine a gift for structure with deep care, so you build the systems and the safe spaces where the people around you can do their best work. You keep things stable so everyone else is free to take their risks. Like the pole star that holds its place while the whole sky turns around it, you're the fixed point people use to find their way. The safety you create is the soil other people's boldness grows in.`,
    starPositions: [
      { x: 20, y: 30, primary: false },
      { x: 35, y: 25, primary: false },
      { x: 50, y: 20, primary: true },
      { x: 65, y: 25, primary: false },
      { x: 80, y: 30, primary: false },
      { x: 25, y: 50, primary: false },
      { x: 40, y: 55, primary: true },
      { x: 60, y: 55, primary: true },
      { x: 75, y: 50, primary: false },
      { x: 50, y: 70, primary: true }
    ],
    connections: [
      { from: 0, to: 1, primary: false },
      { from: 1, to: 2, primary: true },
      { from: 2, to: 3, primary: true },
      { from: 3, to: 4, primary: false },
      { from: 0, to: 5, primary: false },
      { from: 4, to: 8, primary: false },
      { from: 5, to: 6, primary: true },
      { from: 6, to: 7, primary: true },
      { from: 7, to: 8, primary: true },
      { from: 6, to: 9, primary: false },
      { from: 7, to: 9, primary: false }
    ]
  },
  {
    id: 'dreamweaver',
    name: 'Dreamweaver',
    domains: ['vision', 'connection'],
    description: `Dreamweavers imagine better futures and pull other people right into the dream, while most of us are still talking ourselves out of it. You blend bold vision with a gift for connection, so your ideas take root in people's hearts and move them toward building the thing with you. You paint possibilities and make them feel shared, like they already belong to everyone in the room. Like a comet that pulls every eye to the same patch of sky, you get a whole group looking toward the same horizon. The hope you carry has a way of spreading until everyone around you catches it.`,
    starPositions: [
      { x: 30, y: 20, primary: false },
      { x: 70, y: 20, primary: false },
      { x: 20, y: 35, primary: false },
      { x: 40, y: 30, primary: false },
      { x: 60, y: 30, primary: false },
      { x: 80, y: 35, primary: false },
      { x: 25, y: 50, primary: true },
      { x: 50, y: 45, primary: true },
      { x: 75, y: 50, primary: true },
      { x: 35, y: 65, primary: false },
      { x: 65, y: 65, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 0, to: 2, primary: false },
      { from: 0, to: 3, primary: false },
      { from: 1, to: 4, primary: false },
      { from: 1, to: 5, primary: false },
      { from: 2, to: 6, primary: true },
      { from: 3, to: 7, primary: true },
      { from: 4, to: 7, primary: true },
      { from: 5, to: 8, primary: true },
      { from: 6, to: 7, primary: false },
      { from: 7, to: 8, primary: false },
      { from: 6, to: 9, primary: false },
      { from: 8, to: 10, primary: false }
    ]
  },
  {
    id: 'anchor',
    name: 'Anchor',
    domains: ['impact', 'structure'],
    description: `Anchors make change that lasts, the rare ones who push for transformation and also build the structures that keep it in place. While the bold ideas get the headlines, you're the reason they're still working a year later. You ground big ambitions in solid systems, so progress stays put instead of snapping back the moment attention moves on. You're steady and driven at once, the weight that keeps a moving thing from drifting off course. Like the keel that lets a ship carry full sail without tipping over, you give a bold push the stability to go the distance. When people look back and wonder why this one held when so many others didn't, the answer is you.`,
    starPositions: [
      { x: 50, y: 15, primary: false },
      { x: 45, y: 30, primary: false },
      { x: 50, y: 45, primary: false },
      { x: 55, y: 30, primary: false },
      { x: 50, y: 60, primary: true },
      { x: 35, y: 75, primary: true },
      { x: 65, y: 75, primary: true },
      { x: 50, y: 85, primary: false },
      { x: 25, y: 80, primary: false },
      { x: 75, y: 80, primary: false }
    ],
    connections: [
      { from: 0, to: 1, primary: true },
      { from: 1, to: 2, primary: false },
      { from: 2, to: 3, primary: false },
      { from: 2, to: 4, primary: true },
      { from: 4, to: 5, primary: true },
      { from: 4, to: 6, primary: true },
      { from: 4, to: 7, primary: false },
      { from: 5, to: 8, primary: false },
      { from: 6, to: 9, primary: false },
      { from: 5, to: 7, primary: false },
      { from: 6, to: 7, primary: false }
    ]
  }
];

/**
 * Standalone Star Grid Component - For use outside the constellation tab
 * Shows all 8 stars with locked styling for tiers below premium
 * Uses score-based access control (matches Domains tab logic):
 * - Free: top 3 scoring elements within top 2 domains
 * - Enhanced: top 8 scoring elements across all domains (full constellation)
 * - Premium: all 8 visible
 */
const CosmicStarGridStandalone = ({ results, tier, domainColors, onStarClick }) => {
  const [selectedStar, setSelectedStar] = useState(null);
  
  // Get top 2 domains by score for Free tier restrictions
  const topTwoDomainIds = useMemo(() => {
    if (!results?.domains) return [];
    return Object.entries(results.domains)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([domainId]) => domainId);
  }, [results?.domains]);
  
  // Calculate user's top stars
  const userStars = useMemo(() => {
    if (!results?.topPreferences) return [];
    
    const allElements = [...results.topPreferences].sort((a, b) => {
      const scoreA = a.strengthPercentile || (a.score > 1 ? a.score : a.score * 100);
      const scoreB = b.strengthPercentile || (b.score > 1 ? b.score : b.score * 100);
      return scoreB - scoreA;
    });
    
    // Always return top 8 elements
    return allElements.slice(0, 8).map((element, index) => {
      const normalizedScore = element.strengthPercentile || 
        (element.score > 1 ? element.score : element.score * 100);
      
      const elementId = element.id || element.elementId;
      const personDescriptor = getElementDescriptor(elementId);
      
      return {
        ...element,
        personDescriptor,
        score: normalizedScore,
        globalRank: index + 1
      };
    });
  }, [results?.topPreferences]);
  
  // Pre-compute unlocked element IDs using score-based logic (matches Domains tab)
  const unlockedElementIds = useMemo(() => {
    if (tier === USER_TIERS.PREMIUM) return null; // Premium sees all
    if (!results?.topPreferences) return new Set();
    
    const allElements = [...results.topPreferences]
      .map(el => ({
        ...el,
        normalizedScore: el.strengthPercentile || (el.score > 1 ? el.score : el.score * 100)
      }))
      .sort((a, b) => b.normalizedScore - a.normalizedScore);
    
    if (tier === USER_TIERS.ENHANCED) {
      // Enhanced: Top 8 scoring elements across ALL domains (full constellation)
      return new Set(allElements.slice(0, 8).map(el => el.id || el.elementId));
    }
    
    // Free tier: Top 3 scoring elements within top 2 domains only
    const elementsInTop2 = allElements.filter(el => topTwoDomainIds.includes(el.domainId));
    return new Set(elementsInTop2.slice(0, 3).map(el => el.id || el.elementId));
  }, [tier, results?.topPreferences, topTwoDomainIds]);
  
  // Check if a star's score is unlocked based on tier and score rank
  const isStarScoreUnlocked = (star) => {
    if (tier === USER_TIERS.PREMIUM) return true;
    const starId = star.id || star.elementId;
    return unlockedElementIds.has(starId);
  };
  
  // Count elements in each domain for synergy message
  const domainElementCounts = useMemo(() => {
    if (!results?.topPreferences) return {};
    return results.topPreferences.slice(0, 8).reduce((acc, el) => {
      acc[el.domainId] = (acc[el.domainId] || 0) + 1;
      return acc;
    }, {});
  }, [results?.topPreferences]);
  
  // Get synergy message for a domain
  const getDomainSynergyMessage = (domainId) => {
    const count = domainElementCounts[domainId] || 0;
    const domainName = domainId?.charAt(0).toUpperCase() + domainId?.slice(1);
    if (count >= 3) {
      return `You have ${count} elements in ${domainName} — this is a strong theme in your constellation.`;
    } else if (count === 2) {
      return `${count} elements share the ${domainName} domain, forming a connected pair.`;
    }
    return `This element is part of your ${domainName} domain.`;
  };
  
  const handleStarClick = (star, isScoreLocked) => {
    // All stars are clickable - modal shows locked state if score is locked
    setSelectedStar({ ...star, isScoreLocked });
    if (onStarClick) onStarClick(star);
  };
  
  return (
    <div className="cosmic-star-grid-standalone">
      <div className="cosmic-card cosmic-card--professional">
        <div className="cosmic-card__header">
          <h3 className="cosmic-card__title"><CosmicIcon name="brand_spark" size={20} decorative /> Your Constellation Stars</h3>
          <p className="cosmic-card__subtitle">
            Each star represents one of your motivated elements. Tap to learn more.
          </p>
        </div>
        <div className="cosmic-card__body">
          <div className="cosmic-star-grid cosmic-star-grid--row">
            {userStars.map((star, index) => {
              const isScoreLocked = !isStarScoreUnlocked(star);
              const isTopFive = !isScoreLocked && star.globalRank <= 5;
              const isFree = tier === USER_TIERS.FREE;
              
              return (
                <div 
                  key={star.id || index}
                  className={`cosmic-star-grid-item cosmic-star-grid-item--clickable ${selectedStar?.id === star.id ? 'selected' : ''} ${isTopFive ? 'top-five' : ''} ${isScoreLocked ? 'cosmic-star-grid-item--locked' : ''}`}
                  onClick={() => handleStarClick(star, isScoreLocked)}
                  style={{ '--domain-color': domainColors[star.domainId] }}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => e.key === 'Enter' && handleStarClick(star, isScoreLocked)}
                >
                  <div className={`cosmic-star-grid-orb ${isScoreLocked ? 'cosmic-star-grid-orb--locked' : ''}`}>
                    {isScoreLocked ? (
                      <div className="cosmic-star-grid-lock"><CosmicIcon name="ui_locked" size={18} decorative /></div>
                    ) : (
                      <>
                        <div 
                          className="cosmic-star-grid-glow"
                          style={{ 
                            background: domainColors[star.domainId],
                            transform: `scale(${0.8 + (star.score / 100) * 0.4})`
                          }}
                        />
                        <span className="cosmic-star-grid-score">
                          {`#${star.globalRank}`}
                        </span>
                      </>
                    )}
                  </div>
                  {/* Show element name only if unlocked, otherwise show "Locked" */}
                  <div className={`cosmic-star-grid-name ${isScoreLocked ? 'cosmic-star-grid-name--locked' : ''}`}>
                    {isScoreLocked ? <><CosmicIcon name="ui_locked" size={12} decorative /> Locked</> : (star.personDescriptor || star.name)}
                  </div>
                  <div 
                    className={`cosmic-star-grid-domain ${isScoreLocked ? 'cosmic-star-grid-domain--locked' : ''}`}
                    style={{ color: isScoreLocked ? undefined : domainColors[star.domainId] }}
                  >
                    {isScoreLocked ? 'Tap to unlock →' : <><CosmicIcon name={`domain_${star.domainId}`} size={14} decorative /> {star.domainId}</>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Element Detail Modal - handles both unlocked and locked states */}
      {selectedStar && (
        <div className="cosmic-modal-overlay" onClick={() => setSelectedStar(null)}>
          <div className={`cosmic-element-modal ${selectedStar.isScoreLocked ? 'cosmic-element-modal--locked' : ''}`} onClick={e => e.stopPropagation()}>
            <button className="cosmic-modal-close" onClick={() => setSelectedStar(null)}>✕</button>
            
            {/* Star Visual - unlocked shows rank (Free) or score (Enhanced/Premium), locked shows lock icon */}
            <div className="cosmic-modal-star-visual">
              {selectedStar.isScoreLocked ? (
                <div className="cosmic-modal-star-orb cosmic-modal-star-orb--locked">
                  <span className="cosmic-modal-lock-icon"><CosmicIcon name="ui_locked" size={28} decorative /></span>
                </div>
              ) : (
                <>
                  <div 
                    className="cosmic-modal-star-orb"
                    style={{ '--star-color': domainColors[selectedStar.domainId] }}
                  >
                    <span className="cosmic-modal-score">
                      {`#${selectedStar.globalRank}`}
                    </span>
                  </div>
                </>
              )}
            </div>
            
            {/* Element name - only visible if unlocked */}
            <h3 className="cosmic-modal-title">
              {selectedStar.isScoreLocked ? 'Locked Element' : (selectedStar.personDescriptor || selectedStar.name)}
            </h3>
            
            {/* Domain badge - only show domain for unlocked */}
            {!selectedStar.isScoreLocked && (
              <div 
                className="cosmic-modal-domain-badge"
                style={{ '--domain-color': domainColors[selectedStar.domainId] }}
              >
                <CosmicIcon name={`domain_${selectedStar.domainId}`} size={16} mono decorative /> {selectedStar.domainId?.charAt(0).toUpperCase() + selectedStar.domainId?.slice(1)}
              </div>
            )}
            
            {/* Description - only visible if unlocked */}
            {!selectedStar.isScoreLocked && (
              <>
                <p className="cosmic-modal-description">
                  {selectedStar.fullDescription || selectedStar.description || getElementDescription(selectedStar.id || selectedStar.elementId, selectedStar.domainId) ||
                   `This element represents your motivation for ${selectedStar.personDescriptor || selectedStar.name}. It's one of your key drivers in your career constellation.`}
                </p>
                
                {/* Synergy message - shows how elements connect */}
                <div className="cosmic-modal-synergy" style={{ '--domain-color': domainColors[selectedStar.domainId] }}>
                  <span className="cosmic-modal-synergy-icon"><CosmicIcon name="brand_spark" size={16} decorative /></span>
                  <span className="cosmic-modal-synergy-text">{getDomainSynergyMessage(selectedStar.domainId)}</span>
                </div>
              </>
            )}
            
            {/* Strength Score / Rank Section - conditional based on lock state and tier */}
            {selectedStar.isScoreLocked ? (
              <div className="cosmic-modal-locked-section">
                <div className="cosmic-modal-locked-message">
                  Your score for this element is locked
                </div>
                <button className="cosmic-cta cosmic-cta--sm">
                  <CosmicIcon name="ui_bolt" size={16} decorative /> Upgrade to {tier === USER_TIERS.FREE ? 'Enhanced' : 'Premium'} to Unlock
                </button>
              </div>
            ) : tier === USER_TIERS.FREE ? (
              <div className="cosmic-modal-strength">
                <div className="cosmic-modal-strength-label">Your #{selectedStar.globalRank} Strength</div>
                <p style={{ 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: '13px', 
                  margin: '8px 0 0', 
                  lineHeight: '1.4',
                  textAlign: 'center'
                }}>
                  Your top elements are closely ranked — Enhanced measurement reveals precise percentages and true ordering.
                </p>
              </div>
            ) : (
              <div className="cosmic-modal-strength">
                <div className="cosmic-modal-strength-label">Strength Score</div>
                <div className="cosmic-modal-strength-bar">
                  <div 
                    className="cosmic-modal-strength-fill"
                    style={{ 
                      width: `${selectedStar.score}%`,
                      background: `linear-gradient(90deg, ${domainColors[selectedStar.domainId]}, color-mix(in srgb, ${domainColors[selectedStar.domainId]} 70%, white))`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CosmicConstellationTab;
export { CosmicStarGridStandalone };
